import React from 'react'
import styled from 'styled-components'
import { formatMetricValue } from '../utils/zoningHelpers'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
} from './ZoneMetricsTable.styles'
import {
  MetricProgressBar,
  MetricProgressBarTranslations,
} from './MetricProgressBar'

const IndentedRow = styled.tr`
  background-color: ${({ theme }) => `${theme.colors.background.secondary}40`};

  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    padding: ${({ theme }) =>
      `${theme.spacing.xsmall} ${theme.spacing.medium} ${theme.spacing.small} ${theme.spacing.medium}`};
  }
`

const IndentedMetricLabel = styled(MetricLabel)`
  padding-left: ${({ theme }) => theme.spacing.xlarge};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-style: italic;
`

const MetricLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
  width: 100%;
`

const ProgressBarContainer = styled.div`
  width: 200px;
  margin-left: ${({ theme }) => theme.spacing.xlarge};
`

export interface DerivedSurfaceRowProps {
  title: string
  description?: string
  currentValue?: number
  authorizedValue?: number
  unit: string
  progressBarTranslations?: MetricProgressBarTranslations
}

/**
 * Indented row component for derived surface metrics (SBd, SB, SPd, SBPU, SP, VBR)
 * These are calculated from index * STd
 */
export const DerivedSurfaceRow: React.FC<DerivedSurfaceRowProps> = ({
  title,
  // description,
  currentValue,
  authorizedValue,
  unit,
  progressBarTranslations,
}) => {
  // Calculate difference
  const calculateDifference = (): {
    value: number
    isPositive: boolean
  } | null => {
    if (!authorizedValue) {
      return null
    }

    if (authorizedValue === undefined || currentValue === undefined) return null
    const diff = authorizedValue - currentValue
    return { value: diff, isPositive: diff >= 0 }
  }

  const diff = calculateDifference()

  return (
    <IndentedRow>
      <td>
        <MetricLabelContainer>
          <IndentedMetricLabel>{title}</IndentedMetricLabel>
          {currentValue !== undefined && authorizedValue !== undefined && (
            <ProgressBarContainer>
              <MetricProgressBar
                currentValue={currentValue}
                authorizedValue={authorizedValue}
                unit={unit}
                height={6}
                translations={progressBarTranslations}
              />
            </ProgressBarContainer>
          )}
        </MetricLabelContainer>
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, undefined)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {authorizedValue !== undefined ? (
          formatMetricValue(authorizedValue, unit, undefined)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={diff?.isPositive ?? true}>
        {diff ? (
          <>
            {diff.isPositive && '+'}
            {formatMetricValue(diff.value, unit, undefined)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </IndentedRow>
  )
}
